import React, { useState } from "react";
import { 
  Dialog,
  DialogTitle,
  DialogContent,
  Box, 
  TextField, 
  Button, 
  Typography, 
  CircularProgress, 
  Alert,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Divider
} from "@mui/material";
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import axios from "axios";
import { Account } from '../App';

interface LoginDialogProps {
  open: boolean;
  onClose: () => void;
  onLogin: (token: string, userId: string, username: string) => void;
  isAddingAccount?: boolean;
  existingAccounts?: Account[];
}

const LoginDialog: React.FC<LoginDialogProps> = ({ 
  open, 
  onClose, 
  onLogin,
  isAddingAccount = false,
  existingAccounts = []
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const response = await axios.post(`${backendUrl}/api/login`, { email, password });
      if (response.data && response.data.token) {
        // Check if this account is already logged in
        const existingAccount = existingAccounts.find(acc => acc.userId === response.data.userId);
        if (existingAccount) {
          setError("This account is already logged in. You will be switched to it.");
          setTimeout(() => {
            onLogin(response.data.token, response.data.userId, response.data.username);
            handleClose();
          }, 2000);
        } else {
          onLogin(response.data.token, response.data.userId, response.data.username);
          handleClose();
        }
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || "Failed to login. Please try again.";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmail("");
    setPassword("");
    setError("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {isAddingAccount && <PersonAddIcon />}
        {isAddingAccount ? 'Add Account' : 'Login'}
      </DialogTitle>
      
      <DialogContent>
        {isAddingAccount && existingAccounts.length > 0 && (
          <>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Currently logged in accounts:
            </Typography>
            <List dense sx={{ mb: 2 }}>
              {existingAccounts.map(account => (
                <ListItem key={account.userId} sx={{ py: 0.5 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ width: 30, height: 30, fontSize: '0.9rem' }}>
                      {account.username.charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    primary={account.username}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                  {account.isActive && (
                    <Chip label="Active" size="small" color="primary" />
                  )}
                </ListItem>
              ))}
            </List>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Add a new account:
            </Typography>
          </>
        )}

        {error && (
          <Alert severity={error.includes("already logged in") ? "warning" : "error"} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            margin="dense"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
          />
          <TextField
            margin="dense"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isLoading}
          />
          
          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button 
              onClick={handleClose} 
              fullWidth 
              variant="outlined"
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              fullWidth 
              variant="contained" 
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} />
              ) : (
                isAddingAccount ? "Add Account" : "Login"
              )}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default LoginDialog;